import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { siteConfig } from '../../config/siteConfig';
import { useNavigate } from 'react-router-dom';
import { Locale, SUPPORTED_LOCALES } from '@ritabygglov/core';
import { userService } from '../../apiServices/UserService';
import Button, { ButtonType } from '../../components/Button/Button';
import { SelectInput } from '../../components/Input/Input';
import { Flex } from '../../components/Flex/Flex';
import { Sizing } from '../../styles/spacing';
import { ExternalLink } from '../../components/Icons';
import { Navbar } from '../../components/Navbar/Navbar';
import { Text } from '../../components/Text/Text';
import { FontSize } from '../../styles/typography';
import { useLocale } from '../../context/Locale/useLocale';

const LOCALE_OPTIONS: Record<Locale, string> = {
  sv: 'Svenska',
  en: 'English',
};
import { useToast } from '../../context/Toast/useToast';
import { useUser } from '../../context/User/useUser';
import { COOKIE_CONSENT_KEY, getCookie } from '../../helpers/cookies';
import { FeatureFlags, useFeatureFlags } from '../../hooks/useFeatureFlags';
import { disableAnalytics, enableAnalytics } from '../../config/posthog';
import { STRIPE_PORTAL_URL } from '../../config/stripe';
import {
  Title,
  ActiveSubscriptionNotice,
  BackLink,
  ConfirmInput,
  PageContainer,
  Section,
  SectionAction,
  SectionContent,
  SectionTitle,
  SettingsBox,
  StatusText,
} from './UserSettings.styles';

const CONFIRM_PHRASE = 'RADERA';

export default function UserSettings() {
  const user = useUser();
  const navigate = useNavigate();
  const isSandbox = useFeatureFlags(FeatureFlags.SandboxSubscription);
  const { addToast } = useToast();
  const intl = useIntl();

  const { locale } = useLocale();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmValue, setDeleteConfirmValue] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const handleUpdateLocale = async (newLocale: Locale) => {
    const result = await userService.updateLocale(newLocale);
    user.fetchUser();
    addToast({ message: result.message, type: 'success' });
  };

  const stripeUrl = isSandbox
    ? STRIPE_PORTAL_URL.sandbox
    : STRIPE_PORTAL_URL.production;
  const cookieConsent = getCookie(COOKIE_CONSENT_KEY);

  const activeSubscription = user.subscriptions.find(
    (s) => s.status === 'active' || s.status === 'trialing'
  );
  const pastSubscriptions = user.subscriptions.filter(
    (s) => s.status !== 'active' && s.status !== 'trialing'
  );

  const handleToggleMarketing = async (value: boolean) => {
    setMarketingLoading(true);
    try {
      await userService.updateMarketingConsent(value);
      user.fetchUser();
      addToast({
        message: value
          ? intl.formatMessage({ id: 'userSettings.marketingConsentEnabled' })
          : intl.formatMessage({ id: 'userSettings.marketingConsentDisabled' }),
        type: 'success',
      });
    } finally {
      setMarketingLoading(false);
    }
  };

  const handleDisableCookies = () => {
    disableAnalytics();
    addToast({
      message: intl.formatMessage({ id: 'userSettings.cookiesDisabledToast' }),
      type: 'success',
    });
  };

  const handleEnableCookies = () => {
    enableAnalytics();
    addToast({
      message: intl.formatMessage({ id: 'userSettings.cookiesEnabledToast' }),
      type: 'success',
    });
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmValue !== CONFIRM_PHRASE) return;
    setDeleteLoading(true);
    try {
      const result = await userService.deleteSelf();
      addToast({ message: result.message, type: 'success' });
      user.signOut();
      navigate('/sign-in');
    } catch (e: unknown) {
      addToast({
        message:
          e instanceof Error
            ? e.message
            : intl.formatMessage({ id: 'error.somethingWentWrong' }),
        type: 'error',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <PageContainer>
      <Navbar />
      <BackLink to="/">
        <FormattedMessage id="userSettings.backLink" />
      </BackLink>
      <Title style={{ marginBottom: '24px' }}>
        <FormattedMessage id="userSettings.title" />
      </Title>
      <SettingsBox>
        <Section>
          <SectionContent>
            <SectionTitle>
              <FormattedMessage id="userSettings.accountInfo" />
            </SectionTitle>
            <StatusText>{user.name}</StatusText>
            <StatusText>{user.email}</StatusText>
            <StatusText>
              <FormattedMessage
                id="userSettings.memberSince"
                values={{
                  date: new Date(user.createdAt).toLocaleDateString('sv-SE'),
                }}
              />
            </StatusText>
          </SectionContent>
        </Section>

        {user.subscriptions.length > 0 && (
          <Section>
            <SectionContent>
              <SectionTitle>
                <FormattedMessage id="userSettings.subscription" />
              </SectionTitle>
              {activeSubscription ? (
                <Flex $flexDirection="column" $gap={Sizing.xs}>
                  <StatusText>
                    <FormattedMessage
                      id="userSettings.subscriptionStatus"
                      values={{
                        status: (
                          <strong>
                            {activeSubscription.status === 'trialing' ? (
                              <FormattedMessage id="userSettings.subscriptionTrialing" />
                            ) : (
                              <FormattedMessage id="userSettings.subscriptionActive" />
                            )}
                          </strong>
                        ),
                      }}
                    />
                  </StatusText>
                  {activeSubscription.cancelAtPeriodEnd ? (
                    <StatusText>
                      <FormattedMessage
                        id="userSettings.subscriptionCancelsAt"
                        values={{
                          date: new Date(
                            activeSubscription.cancelAt
                          ).toLocaleDateString('sv-SE'),
                        }}
                      />
                    </StatusText>
                  ) : (
                    <StatusText>
                      <FormattedMessage
                        id="userSettings.subscriptionRenewsAt"
                        values={{
                          date: new Date(
                            activeSubscription.currentPeriodEnd
                          ).toLocaleDateString('sv-SE'),
                        }}
                      />
                    </StatusText>
                  )}
                </Flex>
              ) : null}
              {pastSubscriptions.length > 0 && (
                <Flex
                  $flexDirection="column"
                  $gap={Sizing.xs}
                  style={{ marginTop: activeSubscription ? '12px' : '0' }}
                >
                  <Text $fontSize={FontSize.md} $fontWeight="bold">
                    <FormattedMessage id="userSettings.pastSubscriptions" />
                  </Text>
                  {pastSubscriptions.map((sub) => (
                    <StatusText key={sub.subscriptionId}>
                      <FormattedMessage
                        id="userSettings.pastSubscriptionRow"
                        values={{
                          start: new Date(
                            sub.currentPeriodStart
                          ).toLocaleDateString('sv-SE'),
                          end: new Date(
                            sub.currentPeriodEnd
                          ).toLocaleDateString('sv-SE'),
                          status:
                            sub.status === 'canceled'
                              ? intl.formatMessage({
                                  id: 'userSettings.pastSubscriptionCanceled',
                                })
                              : sub.status,
                        }}
                      />
                    </StatusText>
                  ))}
                </Flex>
              )}
              {activeSubscription && (
                <StatusText>
                  <FormattedMessage
                    id="userSettings.noNoticeperiod"
                    values={{ appName: siteConfig.appName }}
                  />
                </StatusText>
              )}
            </SectionContent>
            <SectionAction>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={stripeUrl}
                style={{ textDecoration: 'none' }}
              >
                <Button type={ButtonType.Secondary}>
                  <FormattedMessage id="userSettings.manageSubscription" />{' '}
                  <ExternalLink />
                </Button>
              </a>
            </SectionAction>
          </Section>
        )}

        <Section>
          <SectionContent>
            <SectionTitle>
              <FormattedMessage id="userSettings.language" />
            </SectionTitle>
            <StatusText>
              <FormattedMessage id="userSettings.languageDescription" />
            </StatusText>
          </SectionContent>
          <SectionAction>
            <SelectInput
              aria-label={intl.formatMessage({ id: 'userSettings.language' })}
              value={locale}
              onChange={(e) => handleUpdateLocale(e.target.value as Locale)}
            >
              {SUPPORTED_LOCALES.map((l) => (
                <option key={l} value={l}>
                  {LOCALE_OPTIONS[l]}
                </option>
              ))}
            </SelectInput>
          </SectionAction>
        </Section>

        <Section>
          <SectionContent>
            <SectionTitle>
              <FormattedMessage id="userSettings.cookieConsent" />
            </SectionTitle>
            <StatusText>
              <FormattedMessage
                id="userSettings.cookieConsentDescription"
                values={{
                  appName: siteConfig.appName,
                  termsLink: (chunks) => (
                    <a
                      href={siteConfig.termsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            </StatusText>
          </SectionContent>
          <SectionAction>
            {cookieConsent === 'accepted' ? (
              <Button
                type={ButtonType.Secondary}
                onClick={handleDisableCookies}
              >
                <FormattedMessage id="userSettings.disableCookies" />
              </Button>
            ) : (
              <Button type={ButtonType.Secondary} onClick={handleEnableCookies}>
                <FormattedMessage id="userSettings.enableCookies" />
              </Button>
            )}
          </SectionAction>
        </Section>

        <Section>
          <SectionContent>
            <SectionTitle>
              <FormattedMessage id="userSettings.marketingConsent" />
            </SectionTitle>
            <StatusText>
              <FormattedMessage
                id="userSettings.marketingConsentDescription"
                values={{ appName: siteConfig.appName }}
              />
            </StatusText>
          </SectionContent>
          <SectionAction>
            {user.isAcceptingMarketing ? (
              <Button
                type={ButtonType.Secondary}
                onClick={() => handleToggleMarketing(false)}
                loading={marketingLoading}
              >
                <FormattedMessage id="userSettings.revokeMarketing" />
              </Button>
            ) : (
              <Button
                type={ButtonType.Secondary}
                onClick={() => handleToggleMarketing(true)}
                loading={marketingLoading}
              >
                <FormattedMessage id="userSettings.approveMarketing" />
              </Button>
            )}
          </SectionAction>
        </Section>

        <Section>
          <SectionContent>
            <SectionTitle>
              <FormattedMessage id="userSettings.deleteAccount" />
            </SectionTitle>
            {activeSubscription && !activeSubscription.cancelAtPeriodEnd ? (
              <ActiveSubscriptionNotice>
                <FormattedMessage id="userSettings.activeSubscriptionNotice" />
              </ActiveSubscriptionNotice>
            ) : (
              <>
                <StatusText>
                  <FormattedMessage id="userSettings.deleteAccountDescription" />
                </StatusText>
                {showDeleteConfirm && (
                  <Flex
                    $flexDirection="column"
                    $gap={Sizing.sm}
                    style={{ marginTop: '12px' }}
                  >
                    <Text $fontSize={FontSize.md}>
                      <FormattedMessage
                        id="userSettings.deleteConfirmPrompt"
                        values={{ phrase: <strong>{CONFIRM_PHRASE}</strong> }}
                      />
                    </Text>
                    <ConfirmInput
                      type="text"
                      value={deleteConfirmValue}
                      onChange={(e) => setDeleteConfirmValue(e.target.value)}
                      placeholder={CONFIRM_PHRASE}
                    />
                    <Flex $gap={Sizing.sm}>
                      <Button
                        type={ButtonType.PrimaryDanger}
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmValue !== CONFIRM_PHRASE}
                        loading={deleteLoading}
                      >
                        <FormattedMessage id="userSettings.confirmDeleteButton" />
                      </Button>
                      <Button
                        type={ButtonType.Secondary}
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmValue('');
                        }}
                      >
                        <FormattedMessage id="userSettings.cancelButton" />
                      </Button>
                    </Flex>
                  </Flex>
                )}
              </>
            )}
          </SectionContent>
          <SectionAction>
            {activeSubscription && !activeSubscription.cancelAtPeriodEnd ? (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={stripeUrl}
                style={{ textDecoration: 'none' }}
              >
                <Button type={ButtonType.Secondary}>
                  <FormattedMessage id="userSettings.manageSubscription" />{' '}
                  <ExternalLink />
                </Button>
              </a>
            ) : (
              !showDeleteConfirm && (
                <Button
                  type={ButtonType.SecondaryDanger}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <FormattedMessage id="userSettings.deleteButton" />
                </Button>
              )
            )}
          </SectionAction>
        </Section>
      </SettingsBox>
      <StatusText>
        <FormattedMessage
          id="userSettings.support"
          values={{ contactEmail: siteConfig.contactEmail }}
        />
      </StatusText>
    </PageContainer>
  );
}
